import React from "react";
import styled, { css } from "styled-components";
import { NavLink } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import BasePage from "../../components/BasePage";
import { SettingsRoutes } from "../../routes";
import { useBoolean } from "../../hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faExternalLinkAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { isFeatureEnabled } from "../../helpers";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";

const SettingsLayout = (props: Props) => {
  const { children, user, company } = props;
  const [menuOpen, setMenuOpen] = useBoolean();

  return (
    <BasePage noPadding>
      <NavigationBorderLine />
      <Layout>
        <SettingsNavigationOverlay
          visible={menuOpen}
          onClick={setMenuOpen.off}
        />
        <SettingsNavigation visible={menuOpen}>
          <SettingsCloseButton
            onClick={setMenuOpen.off}
            aria-label={"Close settings menu"}
          >
            <FontAwesomeIcon icon={faTimes} />
          </SettingsCloseButton>

          <NavigationHeading>Your Account</NavigationHeading>
          <NavigationGroup>
            <NavigationItem>
              <NavigationLink
                to={SettingsRoutes.AccountDetails}
                activeClassName="selected"
              >
                Personal Info
              </NavigationLink>
            </NavigationItem>
            {user?.isRecruiter && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.AccountPreferences}
                  activeClassName="selected"
                >
                  Preferences
                </NavigationLink>
              </NavigationItem>
            )}
            {isFeatureEnabled("emailDomainManagement") && user?.isEmployer && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.EmailDomains}
                  activeClassName="selected"
                >
                  Email Domains
                </NavigationLink>
              </NavigationItem>
            )}
          </NavigationGroup>

          <NavigationHeading>Company Profile</NavigationHeading>
          <NavigationGroup>
            {user?.isEmployer && (
              <>
                <ReactTooltip
                  id="view-profile-tooltip"
                  place="right"
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                />
                <NavigationItem>
                  <ExternalNavigationLink href="/profile/user">
                    <span
                      data-for="view-profile-tooltip"
                      data-tip="View Company Profile"
                    >
                      View Profile <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </span>
                  </ExternalNavigationLink>
                </NavigationItem>
              </>
            )}
            <NavigationItem>
              <NavigationLink
                to={SettingsRoutes.CompanyOverview}
                activeClassName="selected"
              >
                Overview
              </NavigationLink>
            </NavigationItem>
            {user?.isEmployer && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.CompanyBanners}
                  activeClassName="selected"
                >
                  Images
                </NavigationLink>
              </NavigationItem>
            )}
            {isFeatureEnabled("companyLocationManagement") && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.CompanyLocation}
                  activeClassName="selected"
                >
                  Location
                </NavigationLink>
              </NavigationItem>
            )}
            {isFeatureEnabled("companySocialsManagement") && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.CompanySocials}
                  activeClassName="selected"
                >
                  Social
                </NavigationLink>
              </NavigationItem>
            )}
            {user?.isEmployer && (company as Employer)?.canUseCustomTerms && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.CompanyCustomTerms}
                  activeClassName="selected"
                >
                  Custom Terms
                </NavigationLink>
              </NavigationItem>
            )}
            {isFeatureEnabled("manageTeamMembers") && user?.isAdmin && (
              <NavigationItem>
                <NavigationLink
                  to={SettingsRoutes.CompanyMembers}
                  activeClassName="selected"
                >
                  Team Members
                </NavigationLink>
              </NavigationItem>
            )}
          </NavigationGroup>
        </SettingsNavigation>

        <Content>
          <SettingsOpenButton
            onClick={setMenuOpen.on}
            aria-label={"Open settings menu"}
          >
            <FontAwesomeIcon icon={faBars} />
          </SettingsOpenButton>

          {children}
        </Content>
      </Layout>
    </BasePage>
  );
};

type Props = {
  user?: RecruiterUser | EmployerUser;
  company?: Recruiter | Employer;
  children: React.ReactNode;
};

const Layout = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  // -60px for the top bar header
  min-height: calc(100vh - 60px);

  @media screen and (max-width: 768px) {
    // -60px + -60px for the top bar header and footer menu
    min-height: calc(100vh - 120px);
  }

  // For the fixed top border line
  padding-top: 1px;
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
`;

const NavigationBorderLine = styled.div`
  position: fixed;
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.gray300};
  z-index: 1;

  // 60px for the top bar header
  top: 60px;
`;

const SettingsMenuButton = styled.button`
  @media screen and (min-width: 767px) {
    display: none;
  }

  border: 0;
  background: none;
  appearance: none;
  padding: 0;
  margin: 0;
`;

const SettingsCloseButton = styled(SettingsMenuButton)`
  padding: 0 2rem 1rem;
  text-align: right;
  display: block;
  width: 100%;
`;

const SettingsOpenButton = styled(SettingsMenuButton)`
  margin-bottom: 1rem;
`;

const SettingsNavigation = styled.div<{ visible: boolean }>`
  background: white;
  flex-basis: 200px;
  border-right: 1px solid ${({ theme }) => theme.colors.gray300};
  padding: 2rem 0;

  @media screen and (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 200px;
    transform: translateX(${({ visible }) => (visible ? "0px" : "-200px")});
    transition: transform 180ms;
  }
`;

const SettingsNavigationOverlay = styled.a<{ visible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: black;
  transition: opacity 180ms;
  opacity: ${({ visible }) => (visible ? 0.6 : 0)};
  pointer-events: ${({ visible }) => (visible ? "all" : "none")};

  @media screen and (min-width: 767px) {
    display: none;
  }
`;

const NavigationHeading = styled.h3`
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 1.5rem;
  padding: 0 2rem;
`;

const NavigationGroup = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  margin-bottom: 2rem;
`;

const NavigationItem = styled.li<{ selected?: boolean }>`
  margin-bottom: 0.5rem;
`;

const NavigationLinkStyles = css`
  display: block;
  font-weight: 500;
  padding: 0 2rem;
  font-size: 14px;

  border-right-width: 3px;
  border-right-style: solid;
  border-right-color: transparent;

  &&& {
    color: ${({ theme }) => theme.colors.gray500};

    &:hover {
      color: ${({ theme }) => theme.colors.gray700};
    }

    &.selected {
      border-right-color: ${({ theme }) => theme.colors.secondary};
      color: ${({ theme }) => theme.colors.gray900};
    }
  }
`;

const NavigationLink = styled(NavLink)`
  ${NavigationLinkStyles}
`;

const ExternalNavigationLink = styled.a`
  ${NavigationLinkStyles}
`;

export default SettingsLayout;
